import React, { useEffect, useState } from "react";
import { Phone, MessageCircle } from "lucide-react";

import { JourneyPillAction } from "@/b2b/components/JourneyMessageScreen";
import {
  SUPPORT_TEL_HREF,
  SUPPORT_WHATSAPP_NUMBER,
} from "@/b2b/config/support";
import config from "@/config";

// KOTS runs its own tenant support, so the "contact CirclePe" affordances must not appear for
// them. Enforced HERE rather than per-screen, so a new dead-end screen cannot forget it.
const HIDE_CONTACT_PARTNERS = ["kots"];

/**
 * Pre-fills the tenant's WhatsApp message so ops receive something actionable immediately,
 * instead of an opening "hi" that costs two more round-trips to qualify.
 *
 * ⚠️ It must carry identity ops can SEARCH. Two things make that non-obvious:
 *   • The screen only knows the magic-link id (e.g. "dmuzci7s"). That is not searchable in
 *     cpbase, so "Reference: dmuzci7s" is unactionable. The LEAD id (CP…) is what ops look up.
 *   • The tenant's WhatsApp number is frequently NOT the number on their application, so the
 *     sender id does not identify them either — hence the explicit "Registered phone" line,
 *     labelled so ops know which number the application is under.
 *
 * Written in the tenant's voice, because the tenant is the one sending it. `problemLine` is the
 * only part that varies per screen, and it is what tells ops which queue the message belongs to.
 */
function buildWhatsappMessage({
  problemLine,
  name,
  phone,
  leadId,
  partner,
  fallbackReference,
}) {
  return encodeURIComponent(
    [
      "Hi CirclePe, I need some help completing my rent application.",
      "",
      problemLine,
      "",
      name ? `Name: ${name}` : null,
      phone ? `Registered phone: ${phone}` : null,
      // Prefer the lead id; fall back to the link id only when identity could not be resolved,
      // so the message is never left with no handle at all.
      leadId
        ? `Reference: ${leadId}`
        : fallbackReference
          ? `Reference: ${fallbackReference}`
          : null,
      partner ? `Property partner: ${partner}` : null,
    ]
      .filter((line) => line !== null)
      .join("\n"),
  );
}

/**
 * "You can also contact us" — WhatsApp and a phone number that answers.
 *
 * Every screen where a tenant's journey has stopped offers the same two ways to reach a human, in
 * the same place, looking the same. It lives in one component because three copies of it is how
 * screens drift apart: the attempt-limit and review screens had it, the salary-not-found screen
 * did not, and nothing made that visible until someone noticed.
 *
 * Paired with equal visual weight, because choosing a channel is the tenant's preference, not a
 * recommendation we should make for them. WhatsApp leads only because it is the dominant support
 * channel for this audience.
 *
 * @param {object} theme       partner theme
 * @param {string} problemLine the tenant's own words for what is wrong, carried into WhatsApp
 * @param {string} partnerName optional partner override; otherwise read from localStorage
 * @param {string} topic       optional allow-listed cpcore support topic (see cpcore
 *                             `support-topics.ts`). When given, tapping EITHER button also raises
 *                             a deduped/cooled-down ops alert — see `notifyOpsOnce` below. Omit it
 *                             (as `SupportEscalationScreen` does) on a screen that already
 *                             escalates on its own, e.g. automatically on mount.
 */
export default function SupportContactActions({
  theme,
  problemLine,
  partnerName,
  topic,
}) {
  const partner = partnerName || localStorage.getItem("partnerIdentifier");
  const userIdentifier = localStorage.getItem("user_identifier");

  // Who the tenant is, resolved by cpcore from the magic-link id. Fetched so the WhatsApp hand-off
  // names them and carries their lead id; a failure degrades the message rather than blocking the
  // screen, which must stay usable precisely when the tenant is already stuck.
  const [identity, setIdentity] = useState(null);

  useEffect(() => {
    if (!userIdentifier) return;
    let cancelled = false;
    fetch(`${config.API_URL}/b2b/public/support-context/${userIdentifier}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setIdentity(data);
      })
      .catch((error) =>
        console.warn("Could not resolve tenant identity for support", error),
      );
    return () => {
      cancelled = true;
    };
  }, [userIdentifier]);

  if (HIDE_CONTACT_PARTNERS.includes((partner || "").toLowerCase())) return null;

  // Fire-and-forget: never block the WhatsApp/tel: redirect on this, and never let a Slack-side
  // failure surface on a screen whose whole job is getting the tenant to a human fast. cpcore's
  // own daily + rolling-cooldown dedupe (see `support-topics.ts`) is the source of truth for "how
  // often" — nothing here needs its own guard against a double-tap.
  const notifyOpsOnce = () => {
    if (!topic) return;
    fetch(`${config.API_URL}/b2b/public/support-request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        topic,
        reason: problemLine || undefined,
        partner: partner || undefined,
        userIdentifier: userIdentifier || undefined,
      }),
    }).catch((error) =>
      console.warn("Could not notify ops about this support tap", error),
    );
  };

  const whatsappHref = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${buildWhatsappMessage({
    problemLine,
    name: identity?.name,
    phone: identity?.phone,
    leadId: identity?.leadId,
    partner: identity?.partner || partner,
    fallbackReference: userIdentifier,
  })}`;

  return (
    <div className="mt-6 w-full">
      <p className={`${theme?.textColor} mb-3 text-center text-xs`}>
        You can also contact us
      </p>
      <div className="grid grid-cols-2 gap-3">
        <JourneyPillAction
          theme={theme}
          href={whatsappHref}
          onClick={notifyOpsOnce}
          external
          icon={<MessageCircle className="h-4 w-4" strokeWidth={1.75} />}
          label="WhatsApp"
        />
        <JourneyPillAction
          theme={theme}
          href={SUPPORT_TEL_HREF}
          onClick={notifyOpsOnce}
          icon={<Phone className="h-4 w-4" strokeWidth={1.75} />}
          label="Call us"
        />
      </div>
    </div>
  );
}
